import { useState, useMemo, useEffect } from 'react';
import { useQueryParams, useDebounce } from './';

/**
 * Custom hook to filter data in the browser
 *
 * @param object[]    items                 Array of objects to filter
 * @param string[]    filterObjects         Array with object keys to filter on. Must match the keys given in items.
 * @param object      filterObjectsValueMap Optional object with values that will be used in the filter matching. Overrides the values given in items.
 * @param string      defaultFilterTerm     Optional filter term that will be used at initial load
 * @param string      idColumn              Optional object key that represents a unique row. This is required only if the data is hierarchial and parents should be included if a child matches the filter.
 * @param string      parentIdColumn        Optional object key that represents a parent row. This is required only if the data is hierarchial and parents should be included if a child matches the filter.
 * @returns object[]  Subset of items which fulfill the filter criteria
 */
export const useFilterableData = (
  items,
  filterObjects = [],
  filterObjectsValueMap = {},
  defaultFilterTerm = '',
  idColumn,
  parentIdColumn
) => {
  const { queryParams, setHistory } = useQueryParams();
  const [internalFilterTerm, setInternalFilterTerm] = useState(
    defaultFilterTerm || queryParams.filter
  );
  const [filterTerm, setFilterTerm] = useState(defaultFilterTerm || queryParams.filter);
  //const [activeFilters, setActiveFilters] = useState({})

  // Populate lookup index
  const idMap = useMemo(() => {
    if (!items) return;
    const m = new Map();
    items.forEach(x => m.set(x[idColumn], x));
    return m;
  }, [items]);

  // Populate lookup index for filterable properties
  const parsedFilterObjects = useMemo(() => {
    let parsed = [];

    // Parse filterObjects
    filterObjects.forEach(object => {
      // Only include selected filterObjects
      if (object.checked) {
        let path = object.value.split('.');

        // Split filter objects if or operator '||' is provided
        path.forEach((p, i) => {
          let subpath = p.split('||');
          let path_tmp = [...path];
          subpath.forEach((sp, j) => {
            if (j === subpath.length - 1) {
              path[i] = sp;
            } else {
              let start = i > 0 ? path_tmp.slice(0, i) : path_tmp[0];
              let end = i < path_tmp.length ? path_tmp.slice(i + 1, path_tmp.length) : [];
              parsed.push({
                ...object,
                value: [...start, sp, ...end].join('.'),
              });
            }
          });
        });

        parsed.push({
          ...object,
          value: path.join('.'),
        });
      }
    });

    return parsed;
  }, [filterObjects]);

  // Recursive function to construct an array with key-value pairs from parsed strings in filterObjects
  const _getValues = (path, item, results = []) => {
    let remainingPath = path;
    let currentItem = item;

    for (let i = 0; i < path.length; ++i) {
      remainingPath = remainingPath.slice(1, remainingPath.length);

      currentItem = currentItem && currentItem[path[i]];

      if (typeof currentItem == 'object' && Array.isArray(currentItem)) {
        currentItem.forEach(currItem => {
          results = _getValues(remainingPath, currItem, results);
        });
      }

      if (remainingPath.length === 0 && currentItem !== undefined && currentItem !== null) {
        // Test for undefined and null, can be "false" if property is boolean
        results.push({ [path[i]]: currentItem });
      }
    }
    return results;
  };

  // Debounce input to reduce number of function calls
  const debouncedSetInternalFilterTerm = useDebounce(setInternalFilterTerm, 200).debounce;

  // Filtered dataset
  const filteredItems = useMemo(() => {
    const query = internalFilterTerm;

    // Return early
    if (!items) return [];
    if (!query) return items;

    const keep = new Set();
    const q = query.toLowerCase();

    // Loop through all items
    items.forEach((item, index) => {
      let match = false;

      // Loop through all searchable properties
      (parsedFilterObjects || []).forEach(object => {
        let path = object.value.split('.');

        // Parse filterObject strings
        let key = _getValues(path, item);

        // Search in array
        if (key && typeof key == 'object' && Array.isArray(key)) {
          match = key.find(x => {
            let [k, v] = Object.entries(x)[0];

            if (typeof v == 'boolean') {
              v = v ? 'true' : 'false';
            }

            return (
              v.toLowerCase().replace(/_/g, ' ').includes(q) ||
              v.toLowerCase().includes(q) ||
              (filterObjectsValueMap[k] &&
                filterObjectsValueMap[k][v].label.toLowerCase().includes(q))
            );
          })
            ? true
            : match;
        }
      });

      // Decide which items to keep
      if (match) {
        if (idColumn) {
          // idColumn is provided. This is required for hierarchial data
          let cur = item;
          while (cur) {
            if (keep.has(cur[idColumn])) break;
            keep.add(cur[idColumn]);
            cur = cur[parentIdColumn] != null ? idMap.get(cur[parentIdColumn]) : undefined; // Make sure parents remain
          }
        } else {
          keep.add(index);
        }
      }
    });

    // Returned filtered items
    return items.filter((x, i) => keep.has(idColumn ? x[idColumn] : i));
  }, [items, internalFilterTerm, parsedFilterObjects, idMap]);

  /*
  const filteredItems = useMemo( () => {

    const filterableItems = items ? [...items] : []
    let filteredItems = filterableItems

    if (internalFilterTerm.length > 1) {

      filteredItems = filterableItems.filter(item => {

        let match = false
        let parsedFilterObjects = []

        // Parse filterObjects
        filterObjects.forEach(object => {

          // Only include selected filterObjects
          if (object.checked) {
            let path = object.value.split('.')
            
            // Split filter objects if or operator '||' is provided
            path.forEach((p, i) => {
              let subpath = p.split('||')
              let path_tmp = [...path]
              subpath.forEach((sp, j) => {

                if (j===subpath.length-1) {
                  path[i] = sp
                } else {
                  
                  let start = i > 0 ? path_tmp.slice(0,i) : path_tmp[0]
                  let end = i < path_tmp.length ? path_tmp.slice(i+1,path_tmp.length) : []
                  parsedFilterObjects.push({
                    ...object,
                    value: [...start, sp, ...end].join('.')
                  })
                }
              })
            })

            parsedFilterObjects.push({
              ...object,
              value: path.join('.')
            })
          }
        })

        // Apply filters
        parsedFilterObjects.forEach(object => {

          let path = object.value.split('.')

          // Parse filterObject strings
          let key = _getValues(path, item)

          // Search in array
          if (key && typeof key == "object" && Array.isArray(key)) {
            match = key.find( (x) => {
              let [k, v] = Object.entries(x)[0]

              if (typeof v == "boolean") {
                v = v ? "true" : "false"
              }

              return v.toLowerCase().replace(/_/g, " ").includes(internalFilterTerm) || 
               v.toLowerCase().includes(internalFilterTerm) ||
               (filterObjectsValueMap[k] && filterObjectsValueMap[k][v].label.toLowerCase().includes(internalFilterTerm))

            }) ? true : match
          }
        })
        return match
    
      })

      // Add parents if nested structure
      const expandableConfigChildren = expandableConfig && expandableConfig.find(x => x.type === 'children')

      if (expandableConfigChildren) {
        const {dataKey, parentDataKey} = expandableConfigChildren
        filteredItems.forEach(x => {
          if (x[parentDataKey] && !filteredItems.find(y => y[dataKey] === x[parentDataKey])) {
            filteredItems.push(filterableItems.find(y => y[dataKey] === x[parentDataKey]))
          }
        })  
      }

    }

    return filteredItems

  }, [items, internalFilterTerm, activeFilters, filterObjects]);
*/

  /**
   * requestFilter
   * @param {string}            query Case-insensitive substring to match on
   */
  const requestFilter = str => {
    //, val) => {

    const string = str || '';
    setFilterTerm(string);
    debouncedSetInternalFilterTerm(string.toLowerCase());

    //val && setActiveFilters(prev => ({...prev, ...val}))

    setHistory({ filter: string });
  };

  return { filteredItems, requestFilter, filterTerm };
};
